import { useState, useContext, useMemo } from "react";
import { Box, Button } from "@mui/material";
import { Layer } from "../../types/layer";
import { DrawingContext, DrawingMode, DrawingFeatureType } from "../../contexts/DrawingContext";
import LayerControlItem from "./LayerControlItem";
import FilterModal, { Filter } from "./FilterModal";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { getAttributesFromLayer } from "../../utils/layerFilters";
import { useAuth } from "../../contexts/AuthContext";
import { isViewOnly } from "../../utils/permissions";

interface NotesControlProps {
  layer: Layer | null;
  filters: Filter[];
  onFiltersChange: (filters: Filter[]) => void;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

const NotesControl: React.FC<NotesControlProps> = ({ layer, filters, onFiltersChange, expanded, onExpandedChange }) => {
  const { drawingMode, setDrawingMode, setDrawingFeatureType } = useContext(DrawingContext);
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  const attributes = useMemo(() => {
    return layer?.rawSource ? getAttributesFromLayer(layer.rawSource) : [];
  }, [layer?.rawSource]);

  if (!layer) return null;

  const handleToggleVisibility = () => {
    layer.toolbarButton?.props?.onClick?.();
  };

  const handleDrawClick = () => {
    if (drawingMode === DrawingMode.NOTE) {
      setDrawingMode(null);
      setDrawingFeatureType(null);
    } else {
      setDrawingMode(DrawingMode.NOTE);
      setDrawingFeatureType(DrawingFeatureType.POINT);
    }
  };

  const handleFiltersChange = (newFilters: Filter[]) => {
    onFiltersChange(newFilters);
  };

  const drawingContent = viewOnly ? null : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Button
        size="small"
        variant={drawingMode === DrawingMode.NOTE ? "contained" : "outlined"}
        onClick={handleDrawClick}
        sx={{
          color: drawingMode === DrawingMode.NOTE ? "#fff" : "#00d4ad",
          borderColor: "#00d4ad",
          backgroundColor: drawingMode === DrawingMode.NOTE ? "#00d4ad" : "transparent",
          "&:hover": {
            backgroundColor: drawingMode === DrawingMode.NOTE ? "#00b899" : "rgba(0, 212, 173, 0.1)",
          },
        }}
      >
        Add Note
      </Button>
    </Box>
  );

  return (
    <>
      <FilterModal
        open={filterModalOpen}
        onClose={() => setFilterModalOpen(false)}
        title="Notes"
        attributes={attributes}
        filters={filters}
        onFiltersChange={handleFiltersChange}
      />
      
      <LayerControlItem
        title="Notes"
        icon={<StickyNote2Icon />}
        visible={layer.active ?? false}
        onToggleVisibility={handleToggleVisibility}
        enabled={!!layer.rawSource}
        filterCount={filters.length}
        onFilterClick={() => setFilterModalOpen(true)}
        filterDisabled={attributes.length === 0}
        drawingContent={drawingContent}
        expanded={expanded}
        onExpandedChange={onExpandedChange}
      />
    </>
  );
};

export default NotesControl;
